import { useMemo } from 'react';
import { useQuery as useApolloQuery } from '@apollo/client/react';
import { GET_AI_VOICES } from '../lib/graphql/queries/voices';

export { normalizeVoiceSelection } from './voiceSelection';

interface GQLAiVoice {
  voiceId: string;
  name: string;
  displayName: string | null;
  provider: string;
  providerVoiceId?: string | null;
  gender: string | null;
  accent: string | null;
  language: string | null;
  sampleAudioUrl: string | null;
  isActive: boolean | null;
  isPremium: boolean | null;
  isDefault: boolean | null;
  useCase: string[] | null;
}

interface GetAiVoicesData {
  aiVoices: {
    aiVoices: GQLAiVoice[];
    total: number;
    hasMore: boolean;
  };
}

export interface AiVoice {
  id: string;
  name: string;
  voice_attribute: string;
  gender: string;
  voice_id: string;
  reduced_latency: boolean;
  displayLabel?: string;
  isRecommended?: boolean;
  sampleAudioUrl?: string | null;
  provider_voice_id?: string | null;
}

function mapGQLVoice(v: GQLAiVoice): AiVoice {
  const attribute = v.accent || '';
  const gender = v.gender || '';
  const isRecommended = v.isDefault === true;
  const displayName = v.displayName || v.name;

  const displayLabel = attribute
    ? `${displayName} - ${attribute} ${gender}${isRecommended ? ' ⭐ (Recommended)' : ''}`
    : `${displayName} - ${gender}${isRecommended ? ' ⭐ (Recommended)' : ''}`;

  return {
    id: v.voiceId,
    name: displayName,
    voice_attribute: attribute,
    gender,
    voice_id: v.voiceId,
    reduced_latency: false,
    displayLabel,
    isRecommended,
    sampleAudioUrl: v.sampleAudioUrl,
    provider_voice_id: v.providerVoiceId,
  };
}

export const useAiVoices = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data, loading, error } = useApolloQuery<GetAiVoicesData>(GET_AI_VOICES, {
    variables: {
      limit: 200,
      offset: 0,
      sortOrder: 'ASC',
      sortBy: 'name',
      isActive: true,
    },
    fetchPolicy: 'cache-first',
    skip: !enabled,
  });

  const voices = useMemo(() => {
    const mapped = (data?.aiVoices?.aiVoices ?? []).map(mapGQLVoice);
    return mapped.sort((a, b) => {
      if (a.isRecommended && !b.isRecommended) return -1;
      if (!a.isRecommended && b.isRecommended) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [data]);

  return { data: voices, isLoading: loading, error };
};
